import { useEffect, useRef, useState, type RefObject } from 'react'

export interface ElementSize {
  width: number
  height: number
}

const ZERO_SIZE: ElementSize = { width: 0, height: 0 }

/**
 * Measures the rendered box of the element `ref` is attached to, live, via `ResizeObserver` — the
 * mechanism behind the dashboard's "self-adjusting" widget density (IB-311 Slice 1). No-ops (stays
 * `{0,0}` forever) in an environment with no `ResizeObserver` — most component tests, and any very
 * old browser — which `classifyDensity` already treats as "assume full until measured".
 */
export function useElementSize<T extends HTMLElement>(): [RefObject<T>, ElementSize] {
  const ref = useRef<T | null>(null)
  const [size, setSize] = useState<ElementSize>(ZERO_SIZE)

  useEffect(() => {
    const node = ref.current
    if (!node || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const box = entry.contentBoxSize
      const first = Array.isArray(box) ? box[0] : (box as unknown as ResizeObserverSize | undefined)
      if (first) {
        setSize({ width: first.inlineSize, height: first.blockSize })
      } else {
        setSize({ width: entry.contentRect.width, height: entry.contentRect.height })
      }
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return [ref, size]
}
